import { useEffect, useRef } from 'react';
import {
  Controller,
  useFieldArray,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SelectField } from '@/components/forms/SelectField';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { WORLD_REGIONS } from '@/lib/constants/world-regions';
import { useCountries } from '@/hooks/useCountries';
import { useStates } from '@/hooks/useStates';
import type { InvestorProfileFormValues } from './schema';

const S = UI_STRINGS.INVESTOR_PROFILE;

const REGION_OPTIONS = WORLD_REGIONS.map((region) => ({ value: region, label: region }));

interface GeographiesSectionProps {
  control: Control<InvestorProfileFormValues>;
  register: UseFormRegister<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
  setValue: UseFormSetValue<InvestorProfileFormValues>;
}

/**
 * The Preferred geographies panel: repeatable region/country/state rows
 * (US-040/US-101 AC4/AC6). The read view is `InvestorGeographiesCard` (MYN-217).
 */
export function GeographiesSection(props: GeographiesSectionProps) {
  return <GeographiesEditor {...props} />;
}

function GeographiesEditor({
  control,
  register,
  errors,
  setValue,
}: {
  control: Control<InvestorProfileFormValues>;
  register: UseFormRegister<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
  setValue: UseFormSetValue<InvestorProfileFormValues>;
}) {
  const { fields, append, remove } = useFieldArray({ control, name: 'geographies' });
  const listError = errors?.geographies?.root?.message ?? errors?.geographies?.message;

  return (
    <Card title={S.GEOGRAPHIES} subtitle={S.GEOGRAPHIES_HINT} headerDivider hoverEffect={false}>
      <div className="flex flex-col gap-4">
        {fields.map((field, index) => (
          <div key={field.id} className="rounded-card bg-page-bg p-6">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-p6 font-semibold text-navy">{S.REGION} {index + 1}</span>
              <Button
                type="button"
                variant="link"
                onClick={() => remove(index)}
                className="text-p6 text-danger hover:text-danger"
              >
                {S.REMOVE}
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <GeographyRowFields
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                index={index}
              />
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="dashed"
        // countryId's schema type is a required `number` (a row needs a country
        // - "Select a country" - when the row is saved), but a brand-new row
        // starts with no selection - `as never` seeds that one blank field.
        onClick={() => append({ worldRegion: '', countryId: undefined as never, stateId: undefined })}
        className="mt-4 inline-flex w-full items-center justify-center gap-2"
      >
        <PlusIcon />
        {S.ADD_GEOGRAPHY}
      </Button>
      {/* A rule on the list as a whole (not on one row) still shows its text. */}
      {listError && (
        <p role="alert" className="text-danger mt-2 text-xs">
          {listError}
        </p>
      )}

    </Card>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 2.5v11M2.5 8h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** One geography row's fields, isolated so watching the row's region doesn't re-render the whole list. */
function GeographyRowFields({
  control,
  register,
  errors,
  setValue,
  index,
}: {
  control: Control<InvestorProfileFormValues>;
  register: UseFormRegister<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
  setValue: UseFormSetValue<InvestorProfileFormValues>;
  index: number;
}) {
  const region = useWatch({ control, name: `geographies.${index}.worldRegion` }) ?? '';
  const countryId = useWatch({ control, name: `geographies.${index}.countryId` });
  const { data: countries = [] } = useCountries();
  const countriesInRegion = countries.filter((country) => country.worldRegion === region);
  const { states } = useStates(countryId ? Number(countryId) : undefined);
  const rowErrors = errors?.geographies?.[index];

  // Disabling a field alone doesn't clear its value - without this, a
  // country/state picked under one region/country survives a switch and
  // still submits in the payload (vf-review MAJOR). Guard each reset on the
  // PREVIOUS value with its OWN ref: a single shared first-render flag is
  // wrong here because React runs both effects on the first commit - the
  // region effect would flip the shared flag, defeating the country effect's
  // guard and wiping a saved `stateId` the moment an existing profile loads.
  const prevRegion = useRef(region);
  const prevCountryId = useRef(countryId);

  useEffect(() => {
    if (prevRegion.current === region) return;
    prevRegion.current = region;
    setValue(`geographies.${index}.countryId`, undefined as never, { shouldDirty: true });
  }, [region, index, setValue]);

  useEffect(() => {
    if (prevCountryId.current === countryId) return;
    prevCountryId.current = countryId;
    setValue(`geographies.${index}.stateId`, undefined, { shouldDirty: true });
  }, [countryId, index, setValue]);

  return (
    <>
      <SelectField
        {...register(`geographies.${index}.worldRegion`)}
        label={S.REGION}
        options={REGION_OPTIONS}
        error={rowErrors?.worldRegion?.message}
      />
      {/* Controller (not register): Country/State options load asynchronously
          (useCountries/useStates), and an uncontrolled native select only
          applies its initial value once, on mount - before an existing
          profile's saved option exists in the DOM yet. Controller keeps the
          select correctly re-rendering once the matching option arrives. */}
      <Controller
        control={control}
        name={`geographies.${index}.countryId`}
        render={({ field }) => (
          <SelectField
            label={S.COUNTRY}
            value={field.value != null ? String(field.value) : ''}
            onChange={(event) =>
              field.onChange(event.target.value ? Number(event.target.value) : undefined)
            }
            onBlur={field.onBlur}
            options={countriesInRegion.map((country) => ({ value: String(country.id), label: country.name }))}
            placeholder={S.SELECT_COUNTRY}
            error={rowErrors?.countryId?.message}
          />
        )}
      />
      <Controller
        control={control}
        name={`geographies.${index}.stateId`}
        render={({ field }) => (
          <SelectField
            label={S.STATE}
            placeholder="—"
            disabled={!countryId || states.length === 0}
            value={field.value != null ? String(field.value) : ''}
            onChange={(event) =>
              field.onChange(event.target.value ? Number(event.target.value) : undefined)
            }
            onBlur={field.onBlur}
            options={states.map((state) => ({ value: String(state.id), label: state.name }))}
            error={rowErrors?.stateId?.message}
          />
        )}
      />
    </>
  );
}
